import React, { useState, useEffect } from "react";
import {
  Button,
  Dropdown,
  Option,
  Label,
  makeStyles,
  tokens,
  Divider,
  Spinner,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import { ArrowCounterclockwise20Regular } from "@fluentui/react-icons";
import { fetchWithIap } from "../utils/iapService";

/**
 * Defines the Fluent UI styles for the DocLibFilter component.
 * Utilizes CSS-in-JS to create scoped class names for layout, dropdowns,
 * tooltips, and responsive wrapping.
 */
const useStyles = makeStyles({
  doclibFilterMain: {
    padding: `12px ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: 0,
  },
  filterSection: {
    display: "flex",
    // flexWrap: "wrap",
    alignItems: "flex-end",
    gap: tokens.spacingHorizontalM,
    width: "100%",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: "1 1 auto ",
    minWidth: 0,  
     maxWidth: "40%" 
  },
  actionSection: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flex: "1 1 auto",
    justifyContent: "flex-end",
    maxWidth: "20%"  
  },
  optionDivider: {
    margin: "4px 0",
  },
  dropdown: {
    minWidth: 0,
    width: "100%",
    "& > button": {
      // Force the button into a block layout to allow text truncation
      display: "block",
      textAlign: "left",
      minWidth: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      paddingRight: "32px", 
      position: "relative",
    },
    
    // Target the expand icon to perfectly position it on the right
    "& > button > span": {
      position: "absolute",
      right: "5px", // Adjust right spacing if needed
      top: "50%",
      transform: "translateY(-50%)",
    },
  },  

  tooltipContent: {
    maxWidth: "300px",
    wordWrap: "break-word",
  },
  dropdownListbox: {
    maxHeight: "200px",
    overflowY: "auto",
  },
  labelText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },  
});

// Constants
const SELECT_ALL_KEY = "select-all";
const DOCUMENT_TYPES = ["IBS", "Protocol", "SVR", "CSRS"];
const CUSTOMER_AREAS = ["Core", "Pathology", "Neurology", "Molecular"];

/**
 * DocLibFilter Component
 * 
 * Renders a set of dropdown filters (Document Type and Customer Area) specifically
 * used when the 'DocLib' data source is active. It fetches available options from the backend
 * and passes the user's selections back to the parent component.
 * 
 * @param {Object} props - The component properties.
 * @param {Function} props.onApply - Callback function triggered whenever the filter selections change.
 * @param {Object} props.initialFilters - The current active filters passed down from the parent state.
 * @param {string} props.sessionId - The current session ID, used for API calls to fetch filter options.
 * @returns {JSX.Element} The rendered DocLibFilter component.
 */
const DocLibFilter = ({
  onApply,
  initialFilters,
  sessionId
}) => {
  const styles = useStyles();

  /**
 * Component State Declarations
 * Manages data for available options, active selections, loading/error states, and UI tooltips.
 */
  const [docTypeOptions, setDocTypeOptions] = useState([]);
  const [customerAreaOptions, setCustomerAreaOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [selectedDocTypes, setSelectedDocTypes] = useState(initialFilters?.docTypes || []);
  const [selectedAreas, setSelectedAreas] = useState(initialFilters?.customerAreas || []);
  const [docTooltipHovered, setDocTooltipHovered] = useState(false);
  const [areaTooltipHovered, setAreaTooltipHovered] = useState(false);
  /**
 * Effect hook to fetch the available filter options from the backend.
 * Runs exactly once when the component initially mounts.
 */
  useEffect(() => {
    const fetchFilterOptions = async () => {
      setIsLoadingOptions(true);
      setOptionsError(null);
      try {
        const response = await fetchWithIap('/api/doclib_filters', {
          method: 'GET',
          headers: {
            'X-Session-ID': sessionId,
          },
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch filters: ${response.statusText}`);
        }
        const data = await response.json();

        // Set the options from the API response, with a fallback to an empty array
        setDocTypeOptions(data.document_types || []);
        setCustomerAreaOptions(data.customer_areas || []);

      } catch (error) {
        console.error("Error fetching DocLib filters:", error);
        setOptionsError("Could not load filter options. Please try again later.");
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchFilterOptions();
  }, []); // Empty dependency array means this runs only once when the component mounts

  /**
 * Effect hook to synchronize the local component state with the `initialFilters` prop.
 * Runs whenever the parent component updates the initialFilters (e.g., from an external reset).
 */
  useEffect(() => {
    setSelectedDocTypes(initialFilters?.docTypes || []);
    setSelectedAreas(initialFilters?.customerAreas || []);
  }, [initialFilters]);

  const isFormValid = selectedDocTypes.length > 0 && selectedAreas.length > 0;

  /**
* Determines the correct array of selected options to pass to the Fluent UI Dropdown.
* Automatically appends the special "Select All" key if every individual option is selected.
* 
* @param {string[]} currentSelection - The array of currently chosen values.
* @param {string[]} allOptions - The master array of all possible values for this dropdown.
* @returns {string[]} The array formatted specifically for the Dropdown UI component.
*/
  const getUiSelectedOptions = (currentSelection, allOptions) => {
    if (allOptions.length > 0 && currentSelection.length === allOptions.length) {
      return [...currentSelection, SELECT_ALL_KEY];
    }
    return currentSelection;
  };

  /**
* Processes selection changes triggered by user interaction with a Dropdown.
* Handles the complex logic for toggling "Select All" vs. individual selections,
* updates the local state, and immediately calls the parent's `onApply` function.
* 
* @param {Object} data - The event data object provided by the Fluent UI Dropdown's onOptionSelect event.
* @param {string[]} currentSelection - The list of items that were selected prior to this event.
* @param {string[]} allOptions - The complete list of available options for this specific dropdown.
* @param {Function} setSelection - The state setter function associated with this dropdown (e.g., setSelectedDocTypes).
* @param {string} type - String identifier specifying which dropdown is being modified ('docType' or 'area').
*/
  const handleSelectionChange = (data, currentSelection, allOptions, setSelection, type) => {
    let newSelection = [];

    if (data.optionValue === SELECT_ALL_KEY) {
      newSelection = currentSelection.length === allOptions.length ? [] : allOptions;
    } else {
      newSelection = data.selectedOptions.filter(item => item !== SELECT_ALL_KEY);
    }

    setSelection(newSelection);
    if (type === 'docType') {
      onApply(
        {
          docTypes: newSelection,
          customerAreas: selectedAreas,
        },
        false
      );
    } else if (type === 'area') {
      onApply(
        {
          docTypes: selectedDocTypes,
          customerAreas: newSelection,
        },
        false
      );
    }
  };

  /**
 * Generates the comma-separated display text string for the Dropdown field and its Tooltip.
 * Shortens the text to "All selected" if the user has picked every available option.
 * 
 * @param {string[]} selection - The user's current array of selected items.
 * @param {string[]} allOptions - The full array of available items to compare against.
 * @returns {string} The formatted string to display in the UI.
 */
  const getDisplayText = (selection, allOptions) => {
    if (allOptions.length > 0 && selection.length === allOptions.length) return "All selected";
    return selection.join(", ");
  };

  /**
 * Submits the user's explicit final filter selections to the parent component.
 * Blocks the submission if the form does not meet validation requirements.
 */
  const handleApply = () => {
    if (!isFormValid) return;
    onApply({
      docTypes: selectedDocTypes,
      customerAreas: selectedAreas,
    }, true);
  };

  /**
 * Clears all local dropdown selections and notifies the parent to reset the global filter state.
 * This effectively disables the filters and triggers empty-state warnings in the app.
 */
  const handleReset = () => {
    setSelectedDocTypes([]);
    setSelectedAreas([]);
    onApply({
      docTypes: [],
      customerAreas: [],
    }, false);
  };


  // Generate display text once to use in both Dropdown and Tooltip
  const docTypesDisplayText = getDisplayText(selectedDocTypes, docTypeOptions);
  const areasDisplayText = getDisplayText(selectedAreas, customerAreaOptions);

  // If there's an error, show an error message instead of the form.
  if (optionsError) {
    return (
      <div className={styles.root}>
        <Text className={styles.errorText}>{optionsError}</Text>
      </div>
    );
  }


  return (
    // The root is now a div styled as our panel
    <div className={styles.doclibFilterMain}>
      {isLoadingOptions ? (
        <Spinner label="Loading filters..." />
      ) : (
        <>
          <div className={styles.filterSection}>
            {/* Document Type Dropdown */}
            <div className={styles.field}>
              <Tooltip content="Document Type" relationship="label">
                <Label className={styles.labelText} required>Document Type</Label>
              </Tooltip> 
              <Tooltip
                content={<div className={styles.tooltipContent}>{docTypesDisplayText}</div>}
                relationship="label"
                visible={selectedDocTypes.length > 0 && docTooltipHovered}
                onVisibleChange={(_, data) => setDocTooltipHovered(data.visible)}
              >
                <Dropdown
                  className={styles.dropdown}
                  listbox={{ className: styles.dropdownListbox }}
                  multiselect={true}
                  placeholder="Select types..."
                  selectedOptions={getUiSelectedOptions(selectedDocTypes, docTypeOptions)}
                  value={getDisplayText(selectedDocTypes, docTypeOptions)}
                  onOptionSelect={(_, data) =>
                    handleSelectionChange(data, selectedDocTypes, docTypeOptions, setSelectedDocTypes, 'docType')
                  }
                >
                  <Option value={SELECT_ALL_KEY} style={{ fontWeight: 'bold' }}>Select All</Option>
                  <Divider className={styles.optionDivider} />
                  {docTypeOptions.map((option) => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Dropdown>
              </Tooltip>
            </div>

            {/* Customer Area Dropdown */}
            <div className={styles.field}>
              <Tooltip content="Customer Area" relationship="label">
                <Label className={styles.labelText} required>Customer Area</Label>
              </Tooltip>
              <Tooltip
                content={<div className={styles.tooltipContent}>{areasDisplayText}</div>}
                relationship="label"
                visible={selectedAreas.length > 0 && areaTooltipHovered}
                onVisibleChange={(_, data) => setAreaTooltipHovered(data.visible)}
              >
                <Dropdown
                  className={styles.dropdown}
                  listbox={{ className: styles.dropdownListbox }}
                  multiselect={true}
                  placeholder="Select areas..."
                  selectedOptions={getUiSelectedOptions(selectedAreas, customerAreaOptions)}
                  value={getDisplayText(selectedAreas, customerAreaOptions)}
                  onOptionSelect={(_, data) =>
                    handleSelectionChange(data, selectedAreas, customerAreaOptions, setSelectedAreas, 'area')
                  }
                >
                  <Option value={SELECT_ALL_KEY} style={{ fontWeight: 'bold' }}>Select All</Option>
                  <Divider className={styles.optionDivider} />
                  {customerAreaOptions.map((option) => (
                    <Option key={option} value={option}>{option}</Option>
                  ))}
                </Dropdown>
              </Tooltip>
            </div>
            {/* Actions Footer */}
            <div className={styles.actionSection}>
              <Tooltip content="Reset Filters" relationship="label">
                <Button
                  appearance="outline"
                  icon={<ArrowCounterclockwise20Regular />}
                  onClick={handleReset}
                >
                </Button>
              </Tooltip>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocLibFilter;



